import { useCallback } from "react";
import { useSelector } from "react-redux";

const DISPLAY_PERMISSION = "DISPLAY";

const usePermissionAccess = () => {
  const user = useSelector((state) => state.AuthReducer.user);
  const permissions = useSelector((state) => state.AuthReducer.permissions);
  const activeLocation = useSelector(
    (state) => state.SlickCareProfilesReducer.activeLocation,
  );

  const hasSubscriptionAccess = useCallback(
    (itemName) => {
      const currentLocation = user?.location?.find(
        (location) => location.id === activeLocation?.id,
      );
      const subscriptionData =
        currentLocation?.subscription_package_details?.item_name?.find(
          (item) => item.item_name === itemName,
        );

      return !!(
        subscriptionData?.id && subscriptionData?.subscription_status !== false
      );
    },
    [activeLocation?.id, user?.location],
  );

  const hasItemAccess = useCallback(
    (itemName, requiredPermission = DISPLAY_PERMISSION) => {
      if (!itemName) return false;
      if (!hasSubscriptionAccess(itemName)) return false;

      if (
        user?.is_master_admin === true ||
        user?.accounting_type === "administrator"
      ) {
        return true;
      }

      const userPermission = user?.permissions
        ?.map((permission) => ({
          ...permission.item_details,
          permission: permissions[permission.permission_id],
        }))
        ?.find((permission) => permission.item_name === itemName);

      return (
        userPermission &&
        permissions.indexOf(userPermission.permission) >=
          permissions.indexOf(requiredPermission)
      );
    },
    [hasSubscriptionAccess, permissions, user],
  );

  const hasBooleanAccess = useCallback(
    (key) => {
      return !!(key && user?.[key] === true);
    },
    [user],
  );

  const hasMenuItemAccess = useCallback(
    (item, requiredPermission = DISPLAY_PERMISSION) => {
      if (!item) return false;

      if (item?.requiresBooleanCheck) {
        return (
          hasBooleanAccess(item?.booleanCheckItem) &&
          hasSubscriptionAccess(item?.item_name)
        );
      }

      if (item?.requiresAnyBooleanAccess?.length) {
        if (!hasSubscriptionAccess(item?.item_name)) return false;
        if (
          user?.is_master_admin === true ||
          user?.accounting_type === "administrator"
        ) {
          return true;
        }
        return item.requiresAnyBooleanAccess.some(
          (key) => user?.[key] === true,
        );
      }

      if (
        item?.only_accessible_to_admin &&
        user?.accounting_type !== "administrator"
      ) {
        return false;
      }

      return hasItemAccess(item.item_name, requiredPermission);
    },
    [hasBooleanAccess, hasItemAccess, hasSubscriptionAccess, user],
  );

  return {
    hasBooleanAccess,
    hasItemAccess,
    hasMenuItemAccess,
    hasSubscriptionAccess,
  };
};

export default usePermissionAccess;
